import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { buildSeo } from "@/lib/seo";
import { SITE } from "@/content/site";
import { LOCATIONS } from "@/content/locations";
import { INDUSTRIES } from "@/content/industries";
import { RANGPUR_AREAS, isAreaIndexable } from "@/content/rangpur-areas";
import { COMPARISONS } from "@/content/comparisons";
import { BN_POSTS } from "@/content/bn/posts";
import { BN_CATEGORY_SLUGS } from "@/content/bn/categories";

type Item = { label: string; to: string };

const CORE: Item[] = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Process", to: "/process" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Free SEO Audit", to: "/free-seo-audit" },
  { label: "Credentials", to: "/credentials" },
  { label: "Proof & Authorship", to: "/proof" },
  { label: "Trust & Security", to: "/trust" },
  { label: "Privacy Policy", to: "/privacy" },
];

const SERVICES: Item[] = [
  { label: "All Services", to: "/services" },
  { label: "Pricing & Packages", to: "/pricing" },
  { label: "Book a Scoping Call", to: "/book" },
  { label: "Answer Engine Optimization (AEO)", to: "/services/aeo" },
  { label: "Generative Engine Optimization (GEO)", to: "/services/geo" },
  { label: "AEO + GEO Bundle", to: "/services/aeo-geo" },
  { label: "Digital Marketing", to: "/services/digital-marketing" },
];

const TOOLS: Item[] = [
  { label: "All Tools", to: "/tools" },
  { label: "SERP Simulator", to: "/tools/serp-simulator" },
  { label: "Schema Generator", to: "/tools/schema-generator" },
  { label: "Robots.txt Tester", to: "/tools/robots-tester" },
  { label: "Free SEO Tools Hub", to: "/seo-tools" },
];

const RESOURCES: Item[] = [
  { label: "Blog", to: "/blog" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Compare SEO Providers", to: "/compare" },
  { label: "AEO Hub", to: "/aeo" },
  { label: "Bangladesh SEO Hub", to: "/bangladesh-seo" },
  { label: "Glossary", to: "/glossary" },
  { label: "AI Answers", to: "/ai-answers" },
  { label: "Resources", to: "/resources" },
  { label: "SEO Expert in Bangladesh", to: "/seo-expert-bangladesh" },
  { label: "What Is Digital Marketing?", to: "/what-is-digital-marketing" },
  { label: "How To Improve Domain Authority", to: "/how-to-improve-domain-authority" },
  { label: "How To Check Domain Authority", to: "/how-to-check-domain-authority" },
  { label: "Moz DA vs Ahrefs DR", to: "/moz-da-vs-ahrefs-dr" },
  { label: "Best Digital Marketing Expert in Bangladesh", to: "/best-digital-marketing-expert-bangladesh" },
  { label: "AEO Checklist (PDF)", to: "/aeo-checklist" },
  { label: "SEO Training", to: "/seo-training" },
];

const BENGALI: Item[] = [
  { label: "বাংলা হোম", to: "/bn" },
  { label: "বাংলা — পরিচিতি", to: "/bn/about" },
  { label: "বাংলা — কেস স্টাডি", to: "/bn/case-studies" },
  { label: "বাংলা ব্লগ", to: "/bn/blog" },
  { label: "বাংলা প্যাকেজ ও দাম", to: "/bn/pricing" },
  { label: "বাংলা — কল বুক করুন", to: "/bn/book" },
  { label: "বাংলা — সেবাসমূহ", to: "/bn/services" },
  { label: "বাংলা — যোগাযোগ", to: "/bn/contact" },
  { label: "বাংলা — শহরভিত্তিক সেবা", to: "/bn/seo-services" },
  ...Object.entries(BN_CATEGORY_SLUGS)
    .filter(([, name]) => BN_POSTS.some((p) => p.category === name))
    .map(([slug, name]) => ({ label: name, to: `/bn/blog/category/${slug}` })),
  ...BN_POSTS.map((p) => ({ label: p.title, to: `/bn/blog/${p.slug}` })),
];

export const Route = createFileRoute("/sitemap")({
  head: () =>
    buildSeo({
      title: "Sitemap — Freelancer Tamal",
      description:
        "Full HTML sitemap of every public page on freelancertamal.com — services, industries, locations, Rangpur neighborhoods, tools, and resources.",
      path: "/sitemap",
      ogEyebrow: "Sitemap",
    }),
  component: SitemapPage,
});

function Section({ title, items }: { title: string; items: Item[] }) {
  if (items.length === 0) return null;
  return (
    <section className="mb-12">
      <h2 className="font-display text-2xl md:text-3xl mb-5">{title}</h2>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
        {items.map((it) => (
          <li key={it.to + it.label}>
            <Link
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              to={it.to as any}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {it.label}
              <ArrowUpRight size={12} className="opacity-0 -ml-1 group-hover:opacity-100 transition-all" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SitemapPage() {
  const industries: Item[] = INDUSTRIES.map((i) => ({
    label: `SEO for ${i.name}`,
    to: `/seo-for/${i.slug}`,
  }));
  industries.unshift({ label: "All Industries", to: "/seo-for" });

  const locations: Item[] = LOCATIONS.map((l) => ({
    label: `SEO in ${l.city}`,
    to: `/seo-services/${l.slug}`,
  }));
  locations.unshift(
    { label: "All Locations", to: "/seo-locations" },
    { label: "City × Service Index", to: "/seo-services" },
  );

  const rangpurAreas: Item[] = RANGPUR_AREAS.filter(isAreaIndexable)
    .map((a) => ({ label: `SEO near ${a.name}`, to: `/seo-services/rangpur/${a.slug}` }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const comparisons: Item[] = COMPARISONS.map((c) => ({
    label: c.title,
    to: `/compare/${c.slug}`,
  }));

  const machineFeeds: Item[] = [
    { label: "XML Sitemap", to: "/sitemap.xml" },
    { label: "XML Sitemap Index (per language)", to: "/sitemap-index.xml" },
    { label: "XML Sitemap — English", to: "/sitemap-en.xml" },
    { label: "XML Sitemap — বাংলা", to: "/sitemap-bn.xml" },
    { label: "RSS Feed", to: "/rss.xml" },
    { label: "JSON Feed", to: "/feed.json" },
    { label: "llms.txt", to: "/llms.txt" },
    { label: "llms-full.txt", to: "/llms-full.txt" },
  ];

  const allSectionNames = [
    "Core",
    "Services",
    "Industries",
    "Locations",
    "Rangpur neighborhoods",
    "Tools",
    "Resources",
    "Comparisons",
    "Bengali",
    "Machine-readable feeds",
  ];

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Sitemap — Freelancer Tamal",
    url: `${SITE.url}/sitemap`,
    description:
      "HTML sitemap of every public page on freelancertamal.com.",
    isPartOf: { "@id": `${SITE.url}/#website` },
    hasPart: allSectionNames.map((name) => ({
      "@type": "WebPageElement",
      name,
    })),
  };

  return (
    <>
      <JsonLd data={collectionSchema} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Sitemap", path: "/sitemap" }]} />
      <header className="container-x pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-6">
          Sitemap
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
          Every page on <span className="text-gradient [overflow-wrap:anywhere]">freelancertamal.com.</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
          A human-readable map of the entire site — services, industries,
          locations, hyperlocal Rangpur pages, tools, and resources. For
          machines, the {" "}
          <Link to="/sitemap.xml" className="text-primary underline-offset-4 hover:underline">
            XML sitemap
          </Link>{" "}
          is what you want.
        </p>
      </header>

      <div className="container-x pb-32">
        <Section title="Core pages" items={CORE} />
        <Section title="Services" items={SERVICES} />
        <Section title="Industries" items={industries} />
        <Section title="Locations" items={locations} />
        <Section title={`Rangpur neighborhoods (${rangpurAreas.length})`} items={rangpurAreas} />
        <Section title="Free tools" items={TOOLS} />
        <Section title="Resources" items={RESOURCES} />
        <Section title="Comparisons" items={comparisons} />
        <Section title="Bengali (বাংলা)" items={BENGALI} />
        <Section title="Machine-readable feeds" items={machineFeeds} />
      </div>
    </>
  );
}